<template>
	<!-- Inside the video view, not a dialog: the view may be in the pop-out window, and a dialog opens in the main window -->
	<div class="screen-picker" role="dialog" :aria-label="t('mycalls.videoScreenPickerTitle')">
		<div class="screen-picker-title">{{ t('mycalls.videoScreenPickerTitle') }}</div>

		<template v-if="request.permission === 'denied'">
			<p class="screen-picker-message">{{ t('mycalls.videoScreenPermission') }}</p>
			<div>
				<Button size="small" :label="t('mycalls.videoScreenPermissionOpen')" @click="emit('openSettings')" />
			</div>
		</template>

		<template v-else>
			<SelectButton v-model="kind" :options="kinds" optionLabel="label" optionValue="value" :allowEmpty="false" size="small" />
			<div v-if="shown.length" class="screen-picker-grid">
				<button v-for="source in shown" :key="source.id" type="button" class="screen-picker-source" :title="source.name" @click="emit('choose', source.id)">
					<img :src="source.thumbnail" alt="" class="screen-picker-thumb">
					<span class="screen-picker-name">
						<img v-if="source.appIcon" :src="source.appIcon" alt="" class="screen-picker-icon">
						<span class="dotdotdot">{{ source.name }}</span>
					</span>
				</button>
			</div>
			<p v-else class="screen-picker-message">{{ t('mycalls.videoScreenPickerEmpty') }}</p>
		</template>

		<div class="screen-picker-footer">
			<Button size="small" severity="secondary" :label="t('common.cancel')" @click="emit('choose', null)" />
		</div>
	</div>
</template>

<script setup lang="ts">
/**
 * The Communicator Desktop app's screen-share source picker (screen-share spec §4.6): the screens and windows the app offers,
 * one click picks, Cancel shares nothing - and without screen-recording permission on macOS, where to give it
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { DisplayMediaRequest, DisplayMediaSource } from '@/stores/Electron.types'

const props = defineProps<{
	/** What the app offers */
	request: DisplayMediaRequest
}>()

const emit = defineEmits<{
	/** The id of the screen or window picked, null for Cancel */
	choose: [id: string | null]
	/** Open macOS' Screen Recording settings */
	openSettings: []
}>()

const { t } = useI18n()

const kind = ref<DisplayMediaSource['kind']>('screen')
const kinds = computed(() => [
	{ label: t('mycalls.videoScreenPickerScreens'), value: 'screen' },
	{ label: t('mycalls.videoScreenPickerWindows'), value: 'window' }
])
const shown = computed(() => props.request.sources.filter(source => source.kind === kind.value))
</script>

<style scoped>
	.screen-picker {
		position: absolute;
		inset: 0;
		z-index: 5; /* over the stage and the controls */
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
		padding: 0.75rem;
		background: var(--bg-panel);
		border-radius: inherit;
		overflow: hidden;
	}

	.screen-picker-title {
		font-weight: 600;
	}

	.screen-picker-grid {
		flex: 1;
		min-height: 0;
		overflow-y: auto;
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(9rem, 1fr));
		gap: 0.5rem;
		align-content: start;
	}

	.screen-picker-source {
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
		padding: 0.25rem;
		border: 1px solid transparent;
		border-radius: 5px;
		background: none;
		color: inherit;
		text-align: left;
		cursor: pointer;
	}

	.screen-picker-source:hover,
	.screen-picker-source:focus-visible {
		border-color: var(--p-primary-color);
	}

	.screen-picker-thumb {
		width: 100%;
		aspect-ratio: 16 / 9;
		object-fit: contain;
		background: #000;
		border-radius: 3px;
	}

	.screen-picker-name {
		display: flex;
		align-items: center;
		gap: 0.25rem;
		min-width: 0;
		font-size: 0.85rem;
	}

	.screen-picker-icon {
		width: 16px;
		height: 16px;
		flex: none;
	}

	.screen-picker-message {
		margin: 0;
	}

	.screen-picker-footer {
		display: flex;
		justify-content: flex-end;
	}
</style>
